import React, { useMemo } from "react";

const MAX_SLIDER_TICKS = 6;

/** Pick exactly `maxTicks` values from a sorted list (first + last + evenly spaced between). */
function buildSliderStops(sorted, maxTicks = MAX_SLIDER_TICKS) {
    if (!sorted.length) return [];
    if (sorted.length <= maxTicks) return sorted;
    const picks = [];
    for (let i = 0; i < maxTicks; i += 1) {
        picks.push(sorted[Math.round((i / (maxTicks - 1)) * (sorted.length - 1))]);
    }
    return [...new Set(picks)].sort((a, b) => a - b);
}

function normalizeAllowedValues(allowedValues) {
    if (!Array.isArray(allowedValues) || allowedValues.length === 0) return null;
    const sorted = [...new Set(allowedValues.map((x) => Math.round(Number(x))).filter((n) => Number.isFinite(n) && n > 0))]
        .sort((a, b) => a - b);
    return sorted.length ? sorted : null;
}

function snapToAllowed(n, allowedSorted) {
    const x = Number(n);
    if (!allowedSorted?.length) return Math.round(x);
    if (!Number.isFinite(x)) return allowedSorted[0];
    const rounded = Math.round(x);
    if (allowedSorted.includes(rounded)) return rounded;
    return allowedSorted.reduce((best, v) => (Math.abs(v - x) < Math.abs(best - x) ? v : best), allowedSorted[0]);
}

/** Up to 6 tick labels — same stops as the slider rail (unless a discrete allowed list is short). */
export function getLeverageSliderTicks(maxLeverage, allowedValues = null) {
    const allowed = normalizeAllowedValues(allowedValues);
    if (allowed) {
        // Margin discrete lists (e.g. 1–10 or [3,5]) — show all when small.
        return allowed.length <= 10 ? allowed : buildSliderStops(allowed, MAX_SLIDER_TICKS);
    }

    const max = Math.max(1, Math.round(Number(maxLeverage) || 125));
    if (max <= 1) return [1];
    if (max === 10) return [1, 5, /* 8, */ 10]; // 8x commented out
    if (max === 20) return [1, 4, 8, 12, 16, 20];
    if (max === 25) return [1, 5, 10, 15, 20, 25];
    if (max === 50) return [1, 10, 20, 30, 40, 50];
    if (max === 75) return [1, 15, 30, 45, 60, 75];
    if (max === 100) return [1, 20, 40, 60, 80, 100];
    if (max === 125) return [1, 25, 50, 75, 100, 125];

    const stops = [];
    for (let i = 0; i < MAX_SLIDER_TICKS; i += 1) {
        stops.push(Math.round(1 + (i / (MAX_SLIDER_TICKS - 1)) * (max - 1)));
    }
    return stops;
}

/** Equal spacing: tick i of n sits at i / (n − 1) along the track. */
export function equalTickPositionPct(tickIndex, tickCount) {
    if (tickCount <= 1) return 0;
    return (tickIndex / (tickCount - 1)) * 100;
}

/**
 * Binance-style leverage control: − / value / + stepper + range slider with tick marks.
 * When `allowedValues` is set (e.g. isolated [1..10] or cross [3,5]), only those values are selectable.
 */
export default function FuturesLeverageSlider({
    value,
    onChange,
    minLeverage = 1,
    maxLeverage = 125,
    allowedValues = null,
    className = "",
}) {
    const allowedSorted = useMemo(() => normalizeAllowedValues(allowedValues), [allowedValues]);

    const max = allowedSorted?.length
        ? allowedSorted[allowedSorted.length - 1]
        : Math.max(1, Number(maxLeverage) || 125);
    const min = allowedSorted?.length
        ? allowedSorted[0]
        : Math.max(1, Number(minLeverage) || 1);

    const clamp = (n) => {
        if (allowedSorted?.length) return snapToAllowed(n, allowedSorted);
        const x = Number(n);
        if (!Number.isFinite(x) || x < min) return min;
        return Math.min(Math.round(x), max);
    };

    const sliderStops = useMemo(() => {
        if (allowedSorted?.length) {
            return allowedSorted.length <= 10
                ? allowedSorted
                : buildSliderStops(allowedSorted, MAX_SLIDER_TICKS);
        }
        return getLeverageSliderTicks(max, null);
    }, [allowedSorted, max]);

    const current = clamp(value);

    const fillPct = max > min ? ((current - min) / (max - min)) * 100 : 0;

    const bump = (delta) => {
        if (allowedSorted?.length) {
            const idx = allowedSorted.indexOf(current);
            const from = idx >= 0 ? idx : allowedSorted.findIndex((v) => v >= current);
            const base = from >= 0 ? from : 0;
            const next = allowedSorted[Math.max(0, Math.min(allowedSorted.length - 1, base + delta))];
            onChange?.(next);
            return;
        }
        onChange?.(clamp(current + delta));
    };

    const onSliderChange = (raw) => {
        onChange?.(clamp(Number(raw)));
    };

    const getTickPct = (x) => {
        if (max <= min) return 0;
        return Math.min(100, Math.max(0, ((x - min) / (max - min)) * 100));
    };

    return (
        <div className={`agce-margin-lvg-slider-block ${className}`.trim()}>
            <div className="agce-margin-lvg-step-row" role="group" aria-label="Leverage stepper">
                <button type="button" onClick={() => bump(-1)} aria-label="Decrease leverage">
                    −
                </button>
                <span className="agce-margin-lvg-step-val" aria-live="polite">
                    {current}x
                </span>
                <button type="button" onClick={() => bump(1)} aria-label="Increase leverage">
                    +
                </button>
            </div>

            <div className="agcx-ft-batch-lvg-slider-wrap agce-ft-lvg-slider-wrap">
                <div className="agce-ft-lvg-slider-rail">
                    <div className="agcx-ft-batch-lvg-track-bg" aria-hidden />
                    <div
                        className="agcx-ft-batch-lvg-track-fill"
                        style={{ width: `${fillPct}%` }}
                        aria-hidden
                    />
                    <input
                        type="range"
                        className="agcx-ft-batch-lvg-range"
                        min={min}
                        max={max}
                        step={1}
                        value={current}
                        onChange={(e) => onSliderChange(Number(e.target.value))}
                        aria-valuemin={min}
                        aria-valuemax={max}
                        aria-valuenow={current}
                        aria-label="Leverage"
                    />
                </div>
                <div className="agcx-ft-batch-lvg-ticks agce-ft-lvg-slider-ticks" aria-hidden>
                    {sliderStops.map((x, i) => {
                        const tickPct = getTickPct(x);
                        const isActive = current >= x;
                        return (
                            <button
                                key={`${x}-${i}`}
                                type="button"
                                className={`agcx-ft-batch-lvg-tick${isActive ? " is-active" : ""}`}
                                style={{ left: `${tickPct}%` }}
                                onClick={() => onChange?.(clamp(x))}
                            >
                                <span className="agcx-ft-batch-lvg-tick-mark" />
                                <span className="agcx-ft-batch-lvg-tick-label">{x}x</span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}
